import { createSignal, For, Show } from "solid-js";
import clsx from "~/util/html/style";
import { uploadInsuranceCard } from "./api";
import { insuranceAnswer, type FormData, type UploadedCard } from "./types";
import { StepActions } from "./_step_actions";
import { useI18n } from "./i18n";

type CardSide = "front" | "back";
const SIDES: CardSide[] = ["front", "back"];
const CARD_FIELD = { front: "insuranceCardFront", back: "insuranceCardBack" } as const;

// Per-side pick counter, kept outside the step because Back and Continue remount it: an upload
// started before the patient left must not land over a newer pick, or over a Remove, when it
// finishes late.
const pickSeq: Record<CardSide, number> = { front: 0, back: 0 };

// The date input's own YYYY-MM-DD, so a string compare orders dates.
const today = () => new Date().toLocaleDateString("en-CA");

export function InsuranceInfoStep(props: {
  formData: FormData;
  /** The step also takes card photos, alongside the typed details, never instead of them. */
  cardUploadEnabled: boolean;
  onUpdate: (partial: Partial<FormData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const t = useI18n();
  type InsuranceErrors = {
    hasInsurance?: string;
    insuranceName?: string;
    insuranceSubscriberName?: string;
    insuranceSubscriberDob?: string;
    insuranceSubscriberId?: string;
    insuranceGroupNumber?: string;
  };

  const [errors, setErrors] = createSignal<InsuranceErrors>({});
  const [uploading, setUploading] = createSignal<CardSide[]>([]);
  const [failed, setFailed] = createSignal<CardSide[]>([]);

  // An existing patient is asked whether their insurance changed; Yes opens the same intake.
  const existingPatient = () => props.formData.patientType === "existing_patient";
  const answer = () => insuranceAnswer(props.formData);
  const setAnswer = (value: "yes" | "no") =>
    props.onUpdate(existingPatient() ? { insuranceChanged: value } : { hasInsurance: value });

  const card = (side: CardSide): UploadedCard | null => props.formData[CARD_FIELD[side]];
  const anyUploading = () => uploading().length > 0;

  // A photo already attached stays until its replacement has landed, so a retake that fails
  // costs nothing.
  const pick = async (side: CardSide, file: File) => {
    const seq = ++pickSeq[side];
    setFailed((sides) => sides.filter((s) => s !== side));
    setUploading((sides) => [...sides, side]);

    const result = await uploadInsuranceCard(file);

    // Superseded by a newer pick or a Remove: this result is nobody's.
    if (seq !== pickSeq[side]) return;
    setUploading((sides) => sides.filter((s) => s !== side));
    if (result.status === "uploaded") {
      const previous = card(side);
      if (previous) URL.revokeObjectURL(previous.previewUrl);
      props.onUpdate({
        [CARD_FIELD[side]]: { signedId: result.signedId, previewUrl: URL.createObjectURL(file) },
      });
    } else {
      setFailed((sides) => [...sides, side]);
    }
  };

  const remove = (side: CardSide) => {
    pickSeq[side]++;
    const current = card(side);
    if (current) URL.revokeObjectURL(current.previewUrl);
    props.onUpdate({ [CARD_FIELD[side]]: null });
    setFailed((sides) => sides.filter((s) => s !== side));
  };

  const validate = (): boolean => {
    if (!answer()) {
      setErrors({ hasInsurance: t("select_option") });
      return false;
    }

    // Every typed field is owed even with photos attached: a photo is read by a person later, and
    // the subscriber's date of birth is on no card.
    if (answer() === "yes") {
      const errs: InsuranceErrors = {};
      if (!props.formData.insuranceName.trim()) errs.insuranceName = t("required");
      if (!props.formData.insuranceSubscriberName.trim())
        errs.insuranceSubscriberName = t("required");
      if (!props.formData.insuranceSubscriberDob) errs.insuranceSubscriberDob = t("required");
      else if (props.formData.insuranceSubscriberDob > today())
        errs.insuranceSubscriberDob = t("future_date");
      if (!props.formData.insuranceSubscriberId.trim()) errs.insuranceSubscriberId = t("required");
      if (!props.formData.insuranceGroupNumber.trim()) errs.insuranceGroupNumber = t("required");
      setErrors(errs);
      return Object.keys(errs).length === 0;
    }

    setErrors({});
    return true;
  };

  const handleContinue = () => {
    if (validate()) props.onNext();
  };

  return (
    <div class="flex flex-col gap-5">
      <h2 class="text-xl font-semibold text-typography-primary">{t("insurance_info")}</h2>

      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-typography-primary">
          {existingPatient() ? t("insurance_changed") : t("have_insurance")}
        </label>
        <div class="flex gap-3">
          <button
            type="button"
            class={clsx(
              "flex-1 rounded-xl border p-4 text-center text-sm font-medium transition-all active:scale-[0.98]",
              answer() === "yes"
                ? "border-brand-primary bg-brand-light ring-1 ring-brand-primary"
                : "border-brand-subtle hover:border-brand-primary/50",
            )}
            onClick={() => setAnswer("yes")}
          >
            {t("yes")}
          </button>
          <button
            type="button"
            class={clsx(
              "flex-1 rounded-xl border p-4 text-center text-sm font-medium transition-all active:scale-[0.98]",
              answer() === "no"
                ? "border-brand-primary bg-brand-light ring-1 ring-brand-primary"
                : "border-brand-subtle hover:border-brand-primary/50",
            )}
            onClick={() => setAnswer("no")}
          >
            {t("no")}
          </button>
        </div>
        <Show when={errors().hasInsurance}>
          <p class="text-xs text-red-600">{errors().hasInsurance}</p>
        </Show>
      </div>

      <Show when={answer() === "yes"}>
        <Show when={props.cardUploadEnabled}>
          <div class="flex flex-col gap-2 rounded-xl border border-brand-subtle p-4">
            <p class="text-sm font-medium text-typography-primary">{t("card_photo_title")}</p>
            <p class="text-xs text-typography-secondary">{t("card_photo_hint")}</p>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <For each={SIDES}>
                {(side) => (
                  <CardPhotoPicker
                    label={side === "front" ? t("card_front") : t("card_back")}
                    card={card(side)}
                    uploading={uploading().includes(side)}
                    failed={failed().includes(side)}
                    onPick={(file) => void pick(side, file)}
                    onRemove={() => remove(side)}
                  />
                )}
              </For>
            </div>
          </div>
        </Show>

        <div class="flex flex-col gap-3">
          <InsuranceField
            label={t("insurance_name")}
            value={props.formData.insuranceName}
            onInput={(v) => props.onUpdate({ insuranceName: v })}
            error={errors().insuranceName}
          />
          <InsuranceField
            label={t("subscriber_name")}
            value={props.formData.insuranceSubscriberName}
            onInput={(v) => props.onUpdate({ insuranceSubscriberName: v })}
            error={errors().insuranceSubscriberName}
          />
          <InsuranceField
            label={t("subscriber_dob")}
            type="date"
            max={today()}
            value={props.formData.insuranceSubscriberDob}
            onInput={(v) => props.onUpdate({ insuranceSubscriberDob: v })}
            error={errors().insuranceSubscriberDob}
          />
          <InsuranceField
            label={t("subscriber_id")}
            value={props.formData.insuranceSubscriberId}
            onInput={(v) => props.onUpdate({ insuranceSubscriberId: v })}
            error={errors().insuranceSubscriberId}
          />
          <InsuranceField
            label={t("group_number")}
            value={props.formData.insuranceGroupNumber}
            onInput={(v) => props.onUpdate({ insuranceGroupNumber: v })}
            error={errors().insuranceGroupNumber}
          />
        </div>
      </Show>

      <StepActions
        onBack={props.onBack}
        onNext={handleContinue}
        nextLabel={anyUploading() ? t("uploading_photo") : t("review")}
        nextDisabled={anyUploading()}
      />
    </div>
  );
}

function CardPhotoPicker(props: {
  label: string;
  card: UploadedCard | null;
  uploading: boolean;
  failed: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
}) {
  const t = useI18n();
  let input: HTMLInputElement | undefined;

  const onChange = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    const file = target.files?.[0];
    // Cleared so picking the same photo again after a failure fires change.
    target.value = "";
    if (file) props.onPick(file);
  };

  return (
    <div class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-typography-secondary">{props.label}</span>
      {/* No `capture`: phones then offer the camera and the photo library both. */}
      <input ref={input} type="file" accept="image/*" class="hidden" onChange={onChange} />
      <Show
        when={props.card}
        fallback={
          <div class="flex flex-col gap-1.5">
            <button
              type="button"
              class="rounded-lg border border-dashed border-brand-subtle px-3 py-3 text-sm font-medium text-typography-primary transition-colors hover:border-brand-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={props.uploading}
              onClick={() => input?.click()}
            >
              {props.uploading
                ? t("uploading_photo")
                : props.failed
                  ? t("card_try_again")
                  : t("add_photo")}
            </button>
            <Show when={props.failed}>
              <p class="text-xs text-red-600">{t("card_upload_failed")}</p>
            </Show>
          </div>
        }
      >
        {(card) => (
          <div class="flex items-center gap-3">
            <img
              src={card().previewUrl}
              alt={props.label}
              class="h-16 w-24 rounded-lg border border-brand-subtle object-cover"
            />
            <button
              type="button"
              class="text-sm font-medium text-typography-secondary underline-offset-2 hover:underline"
              onClick={() => props.onRemove()}
            >
              {t("remove_photo")}
            </button>
          </div>
        )}
      </Show>
    </div>
  );
}

function InsuranceField(props: {
  label: string;
  type?: "text" | "date";
  max?: string;
  value: string;
  onInput: (value: string) => void;
  error?: string;
}) {
  return (
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-typography-primary">{props.label}</label>
      <input
        type={props.type ?? "text"}
        max={props.max}
        class="rounded-lg border border-brand-subtle px-3 py-2.5 text-sm text-typography-primary transition-colors outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-focus"
        value={props.value}
        onInput={(e) => props.onInput(e.currentTarget.value)}
      />
      <Show when={props.error}>
        <p class="text-xs text-red-600">{props.error}</p>
      </Show>
    </div>
  );
}
